import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { Injectable, Injector, inject, TemplateRef, ViewContainerRef, ElementRef } from '@angular/core';
import { filter } from 'rxjs';
import {
    DEFAULT_STATIC_DRAWER_CONFIG,
    StaticDialogCloseResult,
    StaticDialogConfig,
    StaticDrawerConfig,
    StaticDrawerOpenConfig,
    StaticDrawerOutputs,
} from './ic-overlay.config';
import { StaticDialogRef } from './ic-overlay-ref';
import { STATIC_DIALOG_REF } from './ic-overlay.tokens';

@Injectable({ providedIn: 'root' })
export class StaticDialogOverlayService {
    private readonly overlay = inject(Overlay);
    private readonly injector = inject(Injector);

    openDrawer<TComponent extends object, TResult = never>(
        config: StaticDrawerOpenConfig<TComponent>,
    ): StaticDialogRef<TResult | StaticDialogCloseResult> {
        const resolvedConfig = this.resolveDrawerConfig(config);
        const overlayRef = this.overlay.create(this.buildDrawerOverlayConfig(resolvedConfig));
        let componentRef: ReturnType<typeof overlayRef.attach> | null = null;
        const dialogRef = new StaticDialogRef<TResult | StaticDialogCloseResult>(overlayRef, (name, value) => {
            componentRef?.setInput(name, value);
        });

        componentRef = overlayRef.attach(
            new ComponentPortal(
                resolvedConfig.component,
                null,
                Injector.create({
                    parent: resolvedConfig.injector ?? this.injector,
                    providers: [{ provide: STATIC_DIALOG_REF, useValue: dialogRef }],
                }),
            ),
        );

        const inputs = resolvedConfig.inputs;
        if (inputs) {
            for (const [key, value] of Object.entries(inputs)) {
                componentRef.setInput(key, value);
            }
        }

        const outputSubscriptions = this.registerOutputHandlers(componentRef.instance, resolvedConfig.outputs);

        this.registerCloseHandlers(overlayRef, dialogRef, resolvedConfig, outputSubscriptions);

        return dialogRef;
    }

    openConnected<TResult = never>(
        templateRef: TemplateRef<any>,
        viewContainerRef: ViewContainerRef,
        origin: ElementRef | HTMLElement,
        config?: Partial<StaticDialogConfig>
    ): StaticDialogRef<TResult | StaticDialogCloseResult> {
        // Bottom-anchored positions only used to mean: when there isn't enough room below the origin
        // (e.g. a row near the bottom of a scrolled table/dialog), CDK had no "open upward" alternative to
        // fall back to and would instead push the whole panel up the viewport to keep it fully visible —
        // visibly detaching it from its trigger instead of flipping above it. The top-anchored pair below
        // gives it that flip.
        const positionStrategy = this.overlay.position()
            .flexibleConnectedTo(origin)
            .withPositions([
                { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: config?.offsetY ?? 8 },
                { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: config?.offsetY ?? 8 },
                { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -(config?.offsetY ?? 8) },
                { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -(config?.offsetY ?? 8) },
            ])
            // Once a fallback position is chosen on open, keep re-using it (just re-anchored to the
            // origin's current rect) instead of re-evaluating all 4 candidates on every reposition call.
            // Callers like the dynamic-form select field re-run positioning on every animation frame while
            // its ancestor scrolls (to follow origins inside containers CDK's own scroll dispatcher doesn't
            // track) - without a locked position, that many recomputations per second can flip the overlay
            // between the "below" and "above" fallback positions and, per a known CDK quirk, produce a
            // visible flicker-to-invisible right at the flip boundary that looks like the panel closing.
            .withLockedPosition(true)
            // CDK's default flexible dimensions shrink the pane to whatever room is left below the origin.
            // With the position locked, scrolling the origin toward the viewport edge squashed the panel
            // (search box flattened, option list collapsed to 0px). Keep the panel at its natural size and
            // let it travel with its trigger instead.
            .withFlexibleDimensions(false)
            .withPush(false);

        const scrollStrategyType = config?.scrollStrategy ?? 'reposition';
        const scrollStrategy =
            scrollStrategyType === 'close'
                ? this.overlay.scrollStrategies.close()
                : scrollStrategyType === 'block'
                ? this.overlay.scrollStrategies.block()
                : scrollStrategyType === 'noop'
                ? this.overlay.scrollStrategies.noop()
                : this.overlay.scrollStrategies.reposition();

        const overlayConfig = new OverlayConfig({
            hasBackdrop: config?.hasBackdrop ?? true,
            backdropClass: config?.backdropClass ?? 'cdk-overlay-transparent-backdrop',
            panelClass: config?.panelClass,
            positionStrategy,
            scrollStrategy
        });

        const overlayRef = this.overlay.create(overlayConfig);
        const dialogRef = new StaticDialogRef<TResult | StaticDialogCloseResult>(overlayRef);

        overlayRef.attach(new TemplatePortal(templateRef, viewContainerRef, { $implicit: dialogRef }));

        this.registerCloseHandlers(overlayRef, dialogRef, { 
            closeOnBackdropClick: config?.closeOnBackdropClick ?? true, 
            closeOnEscape: config?.closeOnEscape ?? true 
        }, []);

        return dialogRef;
    }

    private resolveDrawerConfig<TComponent extends object>(
        config: StaticDrawerOpenConfig<TComponent>,
    ): StaticDrawerOpenConfig<TComponent> & StaticDrawerConfig<TComponent> {
        return {
            ...DEFAULT_STATIC_DRAWER_CONFIG,
            ...config,
        };
    }

    private buildDrawerOverlayConfig(config: StaticDrawerConfig): OverlayConfig {
        const positionStrategy = this.overlay.position().global().top('0').right('0').bottom('0').left('0');

        return {
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            panelClass: config.panelClass,
            width: config.width,
            height: config.height,
            positionStrategy,
            scrollStrategy: this.overlay.scrollStrategies.block(),
        };
    }

    private registerCloseHandlers<TResult>(
        overlayRef: ReturnType<Overlay['create']>,
        dialogRef: StaticDialogRef<TResult | StaticDialogCloseResult>,
        config: Pick<StaticDialogConfig, 'closeOnBackdropClick' | 'closeOnEscape'>,
        outputSubscriptions: Array<{ unsubscribe(): void }>,
    ): void {
        if (config.closeOnBackdropClick) {
            overlayRef.backdropClick().subscribe(() => {
                dialogRef.close('backdrop');
            });
        }

        if (config.closeOnEscape) {
            overlayRef
                .keydownEvents()
                .pipe(filter((event) => event.key === 'Escape'))
                .subscribe(() => {
                    dialogRef.close('escape');
                });
        }

        overlayRef.detachments().subscribe(() => {
            outputSubscriptions.forEach((subscription) => subscription.unsubscribe());
            dialogRef.close('cancel');
        });
    }

    private registerOutputHandlers<TComponent extends object>(
        componentInstance: TComponent,
        outputs?: StaticDrawerOutputs<TComponent>,
    ): Array<{ unsubscribe(): void }> {
        if (!outputs) {
            return [];
        }

        const subscriptions: Array<{ unsubscribe(): void }> = [];

        for (const [key, handler] of Object.entries(outputs) as Array<[string, (value: unknown) => void]>) {
            if (!handler) {
                continue;
            }

            const candidate = (componentInstance as Record<string, unknown>)[key];
            if (!this.isSubscribable(candidate)) {
                continue;
            }

            const subscription = candidate.subscribe((value: unknown) => {
                handler(value);
            });

            if (subscription && typeof subscription.unsubscribe === 'function') {
                subscriptions.push(subscription);
            }
        }

        return subscriptions;
    }

    private isSubscribable(value: unknown): value is { subscribe(callback: (value: unknown) => void): { unsubscribe(): void } } {
        return typeof value === 'object' && value !== null && 'subscribe' in value && typeof value.subscribe === 'function';
    }
}